import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  enrollCustomerScheme,
  fetchComboProducts,
  fetchIndividualProducts,
} from "../customerSchemeApi";
import { PAYMENT_FREQUENCIES, normalizePositiveNumber } from "../paymentPlanUtils";
import "../CustomerScheme.css";

const OPEN_VALUE_TOTAL_INSTALLMENTS = 11;

const getMessage = (err, fallback) =>
  err?.response?.data?.message || err?.response?.data?.msg || fallback;

const getEnrollmentIdentityKey = (authData, phone) =>
  authData?.customerMasterId || authData?.customerId || phone;

const getComboCode = (item) => item?.comboCode || item?.combo_code || "";
const getComboName = (item) => item?.comboName || item?.description || item?.tier || getComboCode(item);
const getComboFinalAmount = (item) => Number(item?.finalAmount || item?.total_amount || 0);
const getComboMonthlyEmi = (item) => Number(item?.monthlyEmi || item?.emi_1_month || 0);

const getIndividualCode = (item) => item?.productCode || item?.model_code || "";
const getIndividualName = (item) => item?.productName || item?.product_name || getIndividualCode(item);
const getIndividualFinalAmount = (item) => Number(item?.finalAmount || item?.scheme_price || 0);
const getIndividualMonthlyEmi = (item) => Number(item?.monthlyEmi || item?.monthly_emi || 0);
const getApplicationNumber = (item) =>
  item?.applicationNumber || item?.applicationNo || item?.appNo || "";

const sanitizeCustomerName = (value) => {
  const normalized = String(value || "").trim();
  if (!normalized) {
    return "";
  }
  // Avoid showing generic placeholder value coming from auth/session.
  if (normalized.toLowerCase() === "customer") {
    return "";
  }
  return normalized;
};

const normalizeNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};

const roundCurrency = (value) => Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;

const pickFirstPositive = (...values) => {
  for (const value of values) {
    const parsed = normalizeNumber(value);
    if (parsed > 0) {
      return parsed;
    }
  }
  return 0;
};

const getComboDailyEmi = (item) =>
  pickFirstPositive(
    item?.dailyEmi,
    item?.daily_emi,
    item?.emi_1_day,
    item?.emi_daily,
    item?.dailyAmount,
    item?.daily_amount
  );

const getComboWeeklyEmi = (item) =>
  pickFirstPositive(
    item?.weeklyEmi,
    item?.weekly_emi,
    item?.emi_7_days,
    item?.emi_weekly,
    item?.weeklyAmount,
    item?.weekly_amount
  );

const getIndividualDailyEmi = (item) =>
  pickFirstPositive(
    item?.dailyEmi,
    item?.daily_emi,
    item?.emi_1_day,
    item?.emi_daily,
    item?.dailyAmount,
    item?.daily_amount
  );

const getIndividualWeeklyEmi = (item) =>
  pickFirstPositive(
    item?.weeklyEmi,
    item?.weekly_emi,
    item?.emi_7_days,
    item?.emi_weekly,
    item?.weeklyAmount,
    item?.weekly_amount
  );

const extractConfiguredPlanAmount = (source, frequency) => {
  if (!source || typeof source !== "object") {
    return 0;
  }

  const directValue = normalizeNumber(source[frequency]);
  if (directValue > 0) {
    return directValue;
  }

  const nestedValue = normalizeNumber(source[frequency?.toLowerCase?.()]);
  if (nestedValue > 0) {
    return nestedValue;
  }

  const nestedPlan = source?.[frequency] || source?.[frequency?.toLowerCase?.()];
  return normalizeNumber(
    nestedPlan?.amount || nestedPlan?.payableNow || nestedPlan?.selectedPaymentAmount
  );
};

const getConfiguredPaymentPlanOptions = (item) => {
  const source =
    item?.paymentPlanOptions ||
    item?.paymentPlan ||
    item?.paymentPlans ||
    item?.planOptions ||
    null;

  return {
    DAILY: extractConfiguredPlanAmount(source, "DAILY"),
    WEEKLY: extractConfiguredPlanAmount(source, "WEEKLY"),
    MONTHLY: extractConfiguredPlanAmount(source, "MONTHLY"),
  };
};

const EnrollmentPage = () => {
  const navigate = useNavigate();
  const authData = JSON.parse(sessionStorage.getItem("customer_scheme_auth") || "{}");

  const [activeTab, setActiveTab] = useState("COMBO");
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [comboProducts, setComboProducts] = useState([]);
  const [individualProducts, setIndividualProducts] = useState([]);
  const [selectedCode, setSelectedCode] = useState("");
  const [paymentTerm, setPaymentTerm] = useState("MONTHLY");
  const [selectedPaymentAmountInput, setSelectedPaymentAmountInput] = useState("");
  const [monthlyCommitmentAmount, setMonthlyCommitmentAmount] = useState("1000");
  const [planOptions, setPlanOptions] = useState({ DAILY: "", WEEKLY: "", MONTHLY: "" });
  const [installments, setInstallments] = useState([]);
  const [formData, setFormData] = useState({
    customerName: sanitizeCustomerName(authData?.customerName),
    customerPhone: authData?.phone || authData?.mobile || "",
      address: authData?.customerAddress || "",
    employeeName: "",
    employeePhone: "",
    employeeId: "",
  });

  const handleLogout = () => {
    sessionStorage.removeItem("customer_scheme_auth");
    localStorage.removeItem("customer_scheme_auth");
    navigate("/customer/login");
  };

  useEffect(() => {
    const loadDropdowns = async () => {
      try {
        setLoading(true);
        setError("");

        const [comboResponse, individualResponse] = await Promise.all([
          fetchComboProducts(),
          fetchIndividualProducts(),
        ]);

        const comboData = comboResponse?.data?.data || [];
        const individualData = individualResponse?.data?.data || [];

        setComboProducts(Array.isArray(comboData) ? comboData : []);
        setIndividualProducts(Array.isArray(individualData) ? individualData : []);
      } catch (err) {
        setError(getMessage(err, "Unable to load products."));
      } finally {
        setLoading(false);
      }
    };

    loadDropdowns();
  }, []);

  const currentOptions =
    activeTab === "COMBO"
      ? comboProducts
      : activeTab === "INDIVIDUAL"
        ? individualProducts
        : [];

  const selectedProduct = useMemo(() => {
    if (!selectedCode) {
      return null;
    }

    if (activeTab === "COMBO") {
      return comboProducts.find((item) => getComboCode(item) === selectedCode) || null;
    }

    return individualProducts.find((item) => getIndividualCode(item) === selectedCode) || null;
  }, [activeTab, comboProducts, individualProducts, selectedCode]);

  const finalAmount = useMemo(() => {
    if (activeTab === "OPEN_VALUE") {
      return normalizePositiveNumber(monthlyCommitmentAmount) * OPEN_VALUE_TOTAL_INSTALLMENTS;
    }

    if (!selectedProduct) {
      return 0;
    }

    return activeTab === "COMBO"
      ? getComboFinalAmount(selectedProduct)
      : getIndividualFinalAmount(selectedProduct);
  }, [activeTab, monthlyCommitmentAmount, selectedProduct]);

  const termAmountByType = useMemo(() => {
    if (activeTab === "OPEN_VALUE") {
      const monthlyAmount = normalizePositiveNumber(monthlyCommitmentAmount);
      return {
        DAILY: 0,
        WEEKLY: 0,
        MONTHLY: monthlyAmount,
      };
    }

    if (!selectedProduct) {
      return {
        DAILY: 0,
        WEEKLY: 0,
        MONTHLY: 0,
      };
    }

    const configuredOptions = getConfiguredPaymentPlanOptions(selectedProduct);

    const monthly = activeTab === "COMBO"
      ? normalizeNumber(getComboMonthlyEmi(selectedProduct))
      : normalizeNumber(getIndividualMonthlyEmi(selectedProduct));

    const dailyFromApi = activeTab === "COMBO"
      ? normalizeNumber(getComboDailyEmi(selectedProduct))
      : normalizeNumber(getIndividualDailyEmi(selectedProduct));

    const weeklyFromApi = activeTab === "COMBO"
      ? normalizeNumber(getComboWeeklyEmi(selectedProduct))
      : normalizeNumber(getIndividualWeeklyEmi(selectedProduct));

    return {
      DAILY: roundCurrency(configuredOptions.DAILY || dailyFromApi || (monthly > 0 ? monthly / 30 : 0)),
      WEEKLY: roundCurrency(configuredOptions.WEEKLY || weeklyFromApi || (monthly > 0 ? monthly / 4 : 0)),
      MONTHLY: roundCurrency(configuredOptions.MONTHLY || monthly),
    };
  }, [activeTab, selectedProduct, monthlyCommitmentAmount]);

  useEffect(() => {
    setPlanOptions({
      DAILY: String(termAmountByType.DAILY || ""),
      WEEKLY: String(termAmountByType.WEEKLY || ""),
      MONTHLY: String(termAmountByType.MONTHLY || ""),
    });
  }, [termAmountByType.DAILY, termAmountByType.WEEKLY, termAmountByType.MONTHLY, selectedCode]);

  useEffect(() => {
    setSelectedPaymentAmountInput(String(planOptions[paymentTerm] || termAmountByType[paymentTerm] || ""));
  }, [paymentTerm, planOptions, termAmountByType]);

  const normalizedPlanOptions = useMemo(
    () => ({
      DAILY: normalizePositiveNumber(planOptions.DAILY),
      WEEKLY: normalizePositiveNumber(planOptions.WEEKLY),
      MONTHLY: normalizePositiveNumber(planOptions.MONTHLY),
    }),
    [planOptions]
  );

  const selectedTermAmount = normalizePositiveNumber(selectedPaymentAmountInput || planOptions[paymentTerm]);

  const onEnroll = async (e) => {
    e.preventDefault();

    const customerName = formData.customerName.trim();
    if (!customerName || !formData.customerPhone.trim()) {
      setError("Customer name and mobile are required.");
      return;
    }

    if (customerName.toLowerCase() === "customer") {
      setError("Please enter the customer name.");
      return;
    }

    if (!formData.employeeName.trim() || !formData.employeePhone.trim() || !formData.employeeId.trim()) {
      setError("Employee name, employee phone, and employee ID are required for tracking.");
      return;
    }

    if (!/^\d{10}$/.test(formData.employeePhone.trim())) {
      setError("Employee phone must be a valid 10-digit number.");
      return;
    }

    if (activeTab === "COMBO" || activeTab === "INDIVIDUAL") {
      if (!selectedCode) {
        setError(`Please select a ${activeTab === "COMBO" ? "combo" : "product"}.`);
        return;
      }
    }

    if (activeTab === "OPEN_VALUE" && normalizePositiveNumber(monthlyCommitmentAmount) < 1000) {
      setError("Monthly commitment amount must be at least Rs. 1000.");
      return;
    }

    try {
      setSubmitLoading(true);
      setError("");
      setSuccess("");
      setInstallments([]);

      const payload = {
        customerId: authData?.customerId || authData?.customerMasterId || formData.customerPhone.trim(),
        customerMasterId: authData?.customerMasterId || authData?.customerId || formData.customerPhone.trim(),
        customerName: formData.customerName.trim(),
        customerPhone: formData.customerPhone.trim(),
        employeeName: formData.employeeName.trim(),
        employeePhone: formData.employeePhone.trim(),
        employeeId: formData.employeeId.trim(),
        schemeName:
          activeTab === "OPEN_VALUE"
            ? "Savings Scheme"
              : selectedProduct?.scheme ||
                selectedProduct?.comboName ||
                selectedProduct?.description ||
                selectedProduct?.productName ||
                selectedProduct?.product_name ||
                selectedCode,
        totalAmount: finalAmount,
        selectedPaymentFrequency: activeTab === "OPEN_VALUE" ? "MONTHLY" : paymentTerm,
        selectedPaymentAmount:
          activeTab === "OPEN_VALUE"
            ? normalizePositiveNumber(monthlyCommitmentAmount)
            : selectedTermAmount,
        paymentPlanOptions:
          activeTab === "OPEN_VALUE"
            ? {
                DAILY: roundCurrency(normalizePositiveNumber(monthlyCommitmentAmount) / 30),
                WEEKLY: roundCurrency(normalizePositiveNumber(monthlyCommitmentAmount) / 4),
                MONTHLY: normalizePositiveNumber(monthlyCommitmentAmount),
              }
            : normalizedPlanOptions,
      };

      if (activeTab === "COMBO") {
        payload.comboCode = selectedCode;
        payload.combo_code = selectedCode;
      } else if (activeTab === "INDIVIDUAL") {
        payload.productCode = selectedCode;
      } else if (activeTab === "OPEN_VALUE") {
        const monthlyAmount = normalizePositiveNumber(monthlyCommitmentAmount);
        payload.isOpenValuePlan = true;
        payload.planType = "OPEN_VALUE";
        payload.monthlyCommitmentAmount = monthlyAmount;
        payload.fixedMonthlyAmount = monthlyAmount;
        payload.dailyAmount = roundCurrency(monthlyAmount / 30);
        payload.weeklyAmount = roundCurrency(monthlyAmount / 4);
        payload.monthlyAmount = monthlyAmount;
        payload.totalInstallments = OPEN_VALUE_TOTAL_INSTALLMENTS;
      }

      const response = await enrollCustomerScheme(payload);
      const data = response?.data?.data || response?.data || {};
      const enrollmentData = Array.isArray(data)
        ? data[0] || {}
        : data?.enrollment || data;

      setInstallments(Array.isArray(enrollmentData?.installments) ? enrollmentData.installments : []);
      setSuccess("Enrollment successful.");

      const updatedAuth = {
        ...authData,
        customerId: enrollmentData?.customerId || enrollmentData?.customerMasterId || payload.customerId,
        customerMasterId:
          enrollmentData?.customerMasterId ||
          enrollmentData?.customerId ||
          payload.customerMasterId,
        customerName: formData.customerName.trim(),
        phone: formData.customerPhone.trim(),
        mobile: formData.customerPhone.trim(),
        customerAddress: formData.address.trim(),
        applicationNumber: getApplicationNumber(enrollmentData) || authData?.applicationNumber || "",
        isEnrolled: true,
      };
      sessionStorage.setItem("customer_scheme_auth", JSON.stringify(updatedAuth));
      const cacheIdentityKey = getEnrollmentIdentityKey(updatedAuth, formData.customerPhone.trim());
      const cacheKey = `customer_scheme_enrollments_cache_${cacheIdentityKey}`;
      const cachedEnrollments = JSON.parse(localStorage.getItem(cacheKey) || "[]");
      const enrollmentList = Array.isArray(cachedEnrollments) ? cachedEnrollments : [];
      const nextEnrollmentId = String(enrollmentData?._id || enrollmentData?.id || "");
      const mergedEnrollments = [
        enrollmentData,
        ...enrollmentList.filter(
          (item) => String(item?._id || item?.id || "") !== nextEnrollmentId
        ),
      ];
      localStorage.setItem(cacheKey, JSON.stringify(mergedEnrollments));

      setTimeout(() => navigate("/customer/dashboard"), 800);
    } catch (err) {
      setError(getMessage(err, "Enrollment failed."));
    } finally {
      setSubmitLoading(false);
    }
  };

  const noProductsText = loading
    ? ""
    : (activeTab === "COMBO" || activeTab === "INDIVIDUAL") && currentOptions.length === 0
      ? "No products found"
      : "";

  return (
    <div className="customer-scheme-shell">
      <div className="customer-scheme-container">
        <div className="customer-header">
          <div>
            <h2>Enrollment Form</h2>
            <p className="customer-subtitle">Load plans from combo/individual APIs and enroll with one selected code.</p>
          </div>
          <div className="customer-row">
            <button className="customer-btn customer-btn-secondary" onClick={() => navigate("/customer/dashboard")}>Back to Dashboard</button>
            <button className="customer-btn customer-btn-secondary" onClick={handleLogout}>Logout</button>
          </div>
        </div>

        <div className="customer-card">
          <div className="customer-row" style={{ marginBottom: 12 }}>
            <button
              className={`customer-btn ${activeTab === "COMBO" ? "customer-btn-primary" : "customer-btn-secondary"}`}
              onClick={() => {
                setActiveTab("COMBO");
                setSelectedCode("");
                setError("");
              }}
              type="button"
            >
              Combo
            </button>
            <button
              className={`customer-btn ${activeTab === "INDIVIDUAL" ? "customer-btn-primary" : "customer-btn-secondary"}`}
              onClick={() => {
                setActiveTab("INDIVIDUAL");
                setSelectedCode("");
                setError("");
              }}
              type="button"
            >
              Individual Product
            </button>
            <button
              className={`customer-btn ${activeTab === "OPEN_VALUE" ? "customer-btn-primary" : "customer-btn-secondary"}`}
              onClick={() => {
                setActiveTab("OPEN_VALUE");
                setPaymentTerm("MONTHLY");
                setSelectedCode("");
                setError("");
              }}
              type="button"
            >
              Savings Scheme
            </button>
          </div>

          {loading ? (
            <div>Loading products...</div>
          ) : (
            <form className="customer-form" onSubmit={onEnroll}>
              <div className="customer-field">
                <label>Customer Name</label>
                <input
                  value={formData.customerName}
                  onChange={(e) => setFormData((p) => ({ ...p, customerName: e.target.value }))}
                  placeholder="Enter customer name"
                  required
                />
              </div>

              <div className="customer-field">
                <label>Mobile</label>
                <input value={formData.customerPhone} disabled />
              </div>

              <div className="customer-field">
                <label>Address</label>
                <input
                  value={formData.address}
                  onChange={(e) => setFormData((p) => ({ ...p, address: e.target.value }))}
                  placeholder="Enter address"
                />
              </div>

              <div className="customer-grid">
                <div className="customer-field">
                  <label>Employee Name</label>
                  <input
                    value={formData.employeeName}
                    onChange={(e) => setFormData((p) => ({ ...p, employeeName: e.target.value }))}
                    placeholder="Enter employee name"
                  />
                </div>

                <div className="customer-field">
                  <label>Employee Phone</label>
                  <input
                    value={formData.employeePhone}
                    onChange={(e) => setFormData((p) => ({ ...p, employeePhone: e.target.value.replace(/\D/g, "") }))}
                    maxLength={10}
                    placeholder="Enter 10-digit phone"
                  />
                </div>
              </div>

              <div className="customer-field">
                <label>Employee ID</label>
                <input
                  value={formData.employeeId}
                  onChange={(e) => setFormData((p) => ({ ...p, employeeId: e.target.value }))}
                  placeholder="Enter employee ID"
                />
              </div>

              {(activeTab === "COMBO" || activeTab === "INDIVIDUAL") && (
                <>
                  <div className="customer-field">
                    <label>{activeTab === "COMBO" ? "Combo" : "Individual Product"}</label>
                    <select value={selectedCode} onChange={(e) => setSelectedCode(e.target.value)}>
                      <option value="">Select {activeTab === "COMBO" ? "Combo" : "Product"}</option>
                      {activeTab === "COMBO"
                        ? comboProducts.map((item, idx) => (
                            <option key={getComboCode(item) || `combo-${idx}`} value={getComboCode(item)}>
                              {getComboName(item)}
                            </option>
                          ))
                        : individualProducts.map((item, idx) => (
                            <option key={getIndividualCode(item) || `individual-${idx}`} value={getIndividualCode(item)}>
                              {`${getIndividualName(item) || "Product"} (${getIndividualCode(item) || "-"})`}
                            </option>
                          ))}
                    </select>
                    {noProductsText && <div className="customer-helper-text">{noProductsText}</div>}
                  </div>

                  <div className="customer-field">
                    <label>Choose Payment Plan</label>
                    <div className="customer-row" style={{ flexWrap: "wrap" }}>
                      {PAYMENT_FREQUENCIES.map((term) => {
                        const amount = normalizedPlanOptions[term] || 0;
                        const label = term.charAt(0) + term.slice(1).toLowerCase();
                        return (
                          <button
                            key={term}
                            type="button"
                            className={`customer-btn ${paymentTerm === term ? "customer-btn-primary" : "customer-btn-secondary"}`}
                            onClick={() => setPaymentTerm(term)}
                          >
                            {`${label} - Rs. ${amount}`}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="customer-field">
                    <label>Selected Payment Amount</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={selectedPaymentAmountInput}
                      onChange={(e) => {
                        const nextValue = e.target.value;
                        setSelectedPaymentAmountInput(nextValue);
                        setPlanOptions((prev) => ({
                          ...prev,
                          [paymentTerm]: nextValue,
                        }));
                      }}
                      placeholder="Enter selected term amount"
                    />
                  </div>

                  <div className="customer-grid">
                    <div className="customer-field">
                      <label>Final Amount</label>
                      <input value={selectedProduct ? `Rs. ${finalAmount}` : "-"} disabled />
                    </div>
                    <div className="customer-field">
                      <label>Daily Amount</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={planOptions.DAILY}
                        onChange={(e) => setPlanOptions((prev) => ({ ...prev, DAILY: e.target.value }))}
                        disabled={!selectedProduct}
                      />
                    </div>
                    <div className="customer-field">
                      <label>Weekly Amount</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={planOptions.WEEKLY}
                        onChange={(e) => setPlanOptions((prev) => ({ ...prev, WEEKLY: e.target.value }))}
                        disabled={!selectedProduct}
                      />
                    </div>
                    <div className="customer-field">
                      <label>Monthly Amount</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={planOptions.MONTHLY}
                        onChange={(e) => setPlanOptions((prev) => ({ ...prev, MONTHLY: e.target.value }))}
                        disabled={!selectedProduct}
                      />
                    </div>
                    <div className="customer-field">
                      <label>Selected Plan Amount</label>
                      <input value={selectedProduct ? `Rs. ${selectedTermAmount}` : "-"} disabled />
                    </div>
                  </div>
                </>
              )}

              {activeTab === "OPEN_VALUE" && (
                <>
                  <div className="customer-field">
                    <label>Monthly Commitment Amount (minimum Rs. 1000)</label>
                    <input
                      type="number"
                      min="1000"
                      step="1"
                      value={monthlyCommitmentAmount}
                      onChange={(e) => setMonthlyCommitmentAmount(e.target.value)}
                      placeholder="Enter monthly amount"
                    />
                    {monthlyCommitmentAmount !== "" &&
                      normalizePositiveNumber(monthlyCommitmentAmount) < 1000 && (
                        <span style={{ color: "#e53e3e", fontSize: "0.85rem", marginTop: "4px", display: "block" }}>
                          Minimum amount is Rs. 1000. Please enter a higher amount.
                        </span>
                      )}
                  </div>
                  <div className="customer-grid">
                    <div className="customer-field">
                      <label>Duration</label>
                      <input value={`${OPEN_VALUE_TOTAL_INSTALLMENTS} Months`} disabled />
                    </div>
                    <div className="customer-field">
                      <label>Monthly Contribution</label>
                      <input value={`Rs. ${normalizePositiveNumber(monthlyCommitmentAmount)}`} disabled />
                    </div>
                    <div className="customer-field">
                      <label>Wallet Total</label>
                      <input value={`Rs. ${finalAmount}`} disabled />
                    </div>
                  </div>
                </>
              )}

              {error && <div className="customer-error">{error}</div>}
              {success && <div className="customer-success">{success}</div>}

              <div className="customer-row">
                <button
                  className="customer-btn customer-btn-primary"
                  type="submit"
                  disabled={
                    submitLoading ||
                    ((activeTab === "COMBO" || activeTab === "INDIVIDUAL") && !selectedCode) ||
                    (activeTab === "OPEN_VALUE" && normalizePositiveNumber(monthlyCommitmentAmount) < 1000)
                  }
                >
                  {submitLoading ? "Submitting..." : "Submit Enrollment"}
                </button>
              </div>
            </form>
          )}

          {installments.length > 0 && (
            <div className="customer-table-wrap">
              <h3>Returned Payment Schedule</h3>
              <table className="customer-table">
                <thead>
                  <tr>
                    <th>Cycle No</th>
                    <th>Amount</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {installments.map((item, idx) => (
                    <tr key={item?._id || idx}>
                      <td>{item?.installmentNo || item?.installmentNumber || idx + 1}</td>
                      <td>Rs. {Number(item?.amount || 0)}</td>
                      <td>{item?.status || "PENDING"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EnrollmentPage;
